import { Show } from "solid-js";
import type { ConfirmationData, Location } from "./types";
import { useI18n, dateLocale } from "./i18n";
import { localeDate, localeTime } from "~/util/date/intl_dates";
import { SuccessCheck } from "~/common/public_form/success_check";

function generateIcsContent(location: Location, start: Date, end: Date): string {
  const fmt = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Twelve//Online Scheduling//EN",
    "BEGIN:VEVENT",
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:Appointment at ${location.name}`,
    `LOCATION:${location.address ?? ""}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}

export function ConfirmationStep(props: {
  confirmation: ConfirmationData;
  location: Location;
  locale: string;
}) {
  const t = useI18n();
  const dl = () => dateLocale(props.locale);

  const formatDateTime = () => {
    if (!props.confirmation.startTime || !props.confirmation.endTime) return null;

    const start = new Date(props.confirmation.startTime);
    const end = new Date(props.confirmation.endTime);
    const tz = props.location.timezone;

    const dateStr = localeDate(dl(), start, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    });
    const startStr = localeTime(dl(), start, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
    const endStr = localeTime(dl(), end, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });

    return { date: dateStr, time: `${startStr} – ${endStr}` };
  };

  const googleCalUrl = () => {
    if (!props.confirmation.startTime || !props.confirmation.endTime) return null;

    const start = new Date(props.confirmation.startTime);
    const end = new Date(props.confirmation.endTime);

    const fmt = (d: Date) =>
      d
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}/, "");

    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: `Appointment at ${props.location.name}`,
      dates: `${fmt(start)}/${fmt(end)}`,
      location: props.location.address ?? "",
    });

    return `https://www.google.com/calendar/render?${params.toString()}`;
  };

  const downloadIcs = () => {
    if (!props.confirmation.startTime || !props.confirmation.endTime) return;

    const start = new Date(props.confirmation.startTime);
    const end = new Date(props.confirmation.endTime);
    const ics = generateIcsContent(props.location, start, end);

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "appointment.ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div class="flex flex-col items-center gap-6 py-4">
      <SuccessCheck />

      <div class="text-center">
        <h2 class="text-2xl font-semibold text-typography-primary">{t("appointment_requested")}</h2>
        <p class="mt-2 text-sm text-typography-secondary">{t("confirm_message")}</p>
      </div>

      <Show when={formatDateTime()}>
        {(dt) => (
          <div class="w-full rounded-xl bg-brand-light p-5">
            <div class="flex flex-col gap-2.5">
              <Show when={props.confirmation.providerName}>
                {(name) => (
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-typography-secondary">{t("provider")}</span>
                    <span class="text-sm font-medium text-typography-primary">{name()}</span>
                  </div>
                )}
              </Show>
              <div class="flex items-center justify-between">
                <span class="text-sm text-typography-secondary">{t("date")}</span>
                <span class="text-sm font-medium text-typography-primary">{dt().date}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-typography-secondary">{t("time")}</span>
                <span class="text-sm font-medium text-typography-primary">{dt().time}</span>
              </div>
            </div>
          </div>
        )}
      </Show>

      <Show when={props.confirmation.startTime && props.confirmation.endTime}>
        <div class="flex w-full flex-col gap-2">
          <Show when={googleCalUrl()}>
            {(url) => (
              <a
                href={url()}
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 rounded-full border border-brand-subtle px-5 py-2.5 text-sm font-medium text-typography-primary transition-colors hover:bg-brand-light active:scale-[0.98]"
              >
                <svg
                  class="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {t("add_to_google")}
              </a>
            )}
          </Show>
          <button
            type="button"
            class="flex items-center justify-center gap-2 rounded-full border border-brand-subtle px-5 py-2.5 text-sm font-medium text-typography-primary transition-colors hover:bg-brand-light active:scale-[0.98]"
            onClick={downloadIcs}
          >
            <svg
              class="size-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            {t("download_ics")}
          </button>
        </div>
      </Show>

      <Show when={props.location.phone}>
        {(phone) => (
          <p class="text-center text-xs text-typography-secondary">
            {t("questions_call")}{" "}
            <a href={`tel:${phone()}`} class="font-medium text-brand-primary hover:underline">
              {phone()}
            </a>
          </p>
        )}
      </Show>
    </div>
  );
}
